import {
  bug,
  contact,
  cross,
  feature,
  feedback,
} from "../../../../components/FeedbackOverview/SVG";
import QuestLabs from "../../../../components/QuestLabs";
import { hexToGlassColor } from "../../../../general";
import { useFeedbackWorkflow } from "./FeedbackWorkflowContext";
import { ReactNode } from "react";

interface FeedbackOption {
  iconUrl?: string;
  heading?: string;
  description?: string;
}

interface StyleConfig {
  listHover?: {
    iconColor?: string;
    Icon?: {
      color?: string;
    };
    IconSize?: string;
  };
}

interface GeneralFunctionsType {
  fireTrackingEvent: (eventName: string, category: string) => void;
}

export type optionType =
  | "ContactUs"
  | "RequestFeature"
  | "ReportBug"
  | "GeneralFeedback";

interface FeedbackCardContainerProps {
  onClose?: () => void;
  GeneralFunctions: GeneralFunctionsType;
  handleOptionClick?: (optionType: optionType, questId?: string) => void;
  setCardHovered?: React.Dispatch<React.SetStateAction<boolean[]>>;
  cardHovered?: boolean[];
  questIds?: string[];
  title?: string;
  sectionConfig?: any;
}

interface FeedbackCard {
  id: optionType;
  trackingEvent: string;
  trackingCategory: string;
  icon: (color: string, size?: string) => ReactNode;
  defaultHeading: string;
  defaultDescription: string;
  getOptionData: () => FeedbackOption;
}

const FeedbackCardContainer: React.FC<FeedbackCardContainerProps> = ({
  onClose,
  GeneralFunctions,
  handleOptionClick,
  setCardHovered,
  cardHovered,
  questIds,
  title,
  sectionConfig
}) => {
  const { feedbackWorkflowFunctions, themeManager } = useFeedbackWorkflow();
  const styleConfigApi: StyleConfig = {};
  const iconColorApi = "#939393";
  const headers = feedbackWorkflowFunctions.getHeaders();


  const feedbackCards: FeedbackCard[] = [
    {
      id: "GeneralFeedback",
      trackingEvent: "quest_feedback_workflow_general_feedback_clicked",
      trackingCategory: "feedback_workflow_general_feedback",
      icon: feedback,
      defaultHeading: "General Feedback",
      defaultDescription: "Give general feedback on this page",
      getOptionData: () => {
        return feedbackWorkflowFunctions.getGeneralFeedback() || {};
      },
    },
    {
      id: "ReportBug",
      trackingEvent: "quest_feedback_workflow_report_bug_clicked",
      trackingCategory: "feedback_workflow_report_bug",
      icon: bug,
      defaultHeading: "Report a Bug",
      defaultDescription: "Let us know what's broken",
      getOptionData: () => {
        return feedbackWorkflowFunctions.getReportBug() || {};
      },
    },
    {
      id: "RequestFeature",
      trackingEvent: "quest_feedback_workflow_request_feature_clicked",
      trackingCategory: "feedback_workflow_request_feature",
      icon: feature,
      defaultHeading: "Request a Feature",
      defaultDescription: "Tell us how we can improve",
      getOptionData: () => {
        return feedbackWorkflowFunctions.getRequestFeature() || {};
      },
    },
    {
      id: "ContactUs",
      trackingEvent: "quest_feedback_workflow_contactus_clicked",
      trackingCategory: "feedback_workflow_contactus",
      icon: contact,
      defaultHeading: "Contact us",
      defaultDescription: "Tell us how we can help",
      getOptionData: () => {
        return feedbackWorkflowFunctions.getContactUs() || {};
      },
    },
  ];

  const renderFeedbackCard = (card: FeedbackCard, index: number) => {
    const isHovered = cardHovered?.[index] ?? false;
    const optionData = card.getOptionData();
    let val = card.id === "GeneralFeedback" ? "generalFeedback" : card.id === "ReportBug" ? "reportBug" : card.id === "RequestFeature" ? "requestFeature" : card.id === "ContactUs" ? "contactUs" : "";

    if (!sectionConfig[val]) return null;
    return (
      <div
        key={card.id}
        onClick={() => {
          GeneralFunctions.fireTrackingEvent(
            card.trackingEvent,
            card.trackingCategory
          );
          handleOptionClick?.(card.id, questIds?.[index]);
        }}
        className="q-hover q-fw-cards"
        onMouseEnter={() => {
          const newHoverState = Array(4).fill(false);
          newHoverState[index] = true;
          setCardHovered?.(newHoverState);
        }}
        onMouseLeave={() => setCardHovered?.(Array(4).fill(false))}
        style={{
          background: isHovered
            ? themeManager.getListHoverCardStyle().background
            : "transparent",
          borderRadius: "8px",
        }}
      >
        <div
          className="q_feedback_icon"
          style={{
            background: isHovered
              ? themeManager?.getListHoverIconStyle().background
              : themeManager?.getListHoverIconDefaultStyle().background,
            ...themeManager?.getListHoverIcon(),

          }}
        >
          {optionData?.iconUrl ? (
            <img
              className="q_feedback_icon_imgurl"
              src={optionData.iconUrl}
              alt={card.defaultHeading}
            />
          ) : (
            card.icon(
              isHovered
                ? themeManager?.getListHoverSvg().color || "#9035FF"
                : iconColorApi,
              styleConfigApi.listHover?.IconSize
            )
          )}
        </div>
        <div>
          <div
            className="q-fw-tab-heading"
            style={{
              color: isHovered
                ? themeManager.getListHoverHeadingStyle1().color
                : themeManager.getListHoverHeadingStyle2().color,
              ...themeManager?.getListHeadingStyle(),
            }}
          >
            {optionData?.heading || headers?.[index]?.heading || card.defaultHeading}
          </div>
          <div
            className="q-fw-tab-description"
            style={{
              color: isHovered
                ? themeManager?.getListHoverDescriptionStyle1().color
                : themeManager.getListHoverDescriptionStyle2().color,
              ...themeManager.getListDescriptionStyle(),
            }}
          >
            {optionData?.description || headers?.[index]?.subHeading || card.defaultDescription}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="q-fw-crossBtn">
        <p className="q-fw-tab-heading">{title || ''}</p>
        <div onClick={onClose}>
          {cross(feedbackWorkflowFunctions.geticonColor())}
        </div>
      </div>
      <div className="q-fw-content-box">
        {feedbackCards.map((card, index) => renderFeedbackCard(card, index))}
      </div>
      <div>
        {feedbackWorkflowFunctions.getshowFooter() && (
          <QuestLabs
            style={themeManager?.getFooterStyles()?.FooterStyle}
            textStyle={themeManager?.getFooterStyles()?.FooterTextStyle}
            iconStyle={themeManager?.getFooterStyles()?.FooterIcon}
            text={themeManager?.getFooterStyles()?.FooterText}
            link={themeManager?.getFooterStyles()?.FooterLink}
          />
        )}
      </div>
    </div>
  );
};

export default FeedbackCardContainer;